import {
	CalendarDays, Zap, Eye, Heart, MapPin, MessageSquare,
	Phone, Globe, Users, Package, Award, Truck,	BadgeCheck,
	Clock, Trophy, CalendarPlus, AlertCircle, GraduationCap, ShieldCheck,
} from "lucide-react";
import ReportButton from "@/components/ReportButton";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { categoriesAPI, favoriteAPI, messageAPI } from "@/services/api";
import { createTranslationMap } from "@/utils/i18nHelpers";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { resolveApi } from "@/utils/adUtil";
import SellerRatingBadge from "@/components/SellerRatingBadge";
import { formatEventDate } from "@/utils/dateUtil";
import { downloadICS } from "@/utils/ics";
import WaiverCheckBox from "@/components/WaiverCheckBox";

export default function StickyInfoCard ({
																					ad,
																					adType,
																					is_favorite,
																					pricing
}) {

	const { t, i18n } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [catMap, setCatMap] = useState({});
	const [isFav, setIsFav] = useState(is_favorite);
	const [message, setMessage] = useState('');
	const [sending, setSending] = useState(false);
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);

	const OTHER = '__other__';

	useEffect(() => {
		categoriesAPI.list()
			.then(r => setCatMap(createTranslationMap(r.data || [], i18n.language)))
			.catch(() => {});
	}, [i18n.language]);

	const toggleFav = async () => {
		if (!user) return navigate('/auth');
		try {
			if (isFav) {
				await favoriteAPI.remove(ad.id);
				setIsFav(false);
				toast.success(t('common.removed_fav'));
			} else {
				await favoriteAPI.add(ad.id, adType);
				setIsFav(true);
				toast.success(t('common.add_fav'));
			}
		} catch {
			toast.error(t('common.error_fav'));
		}
	};

	const sendMessage = async () => {
		if (!user) return navigate('/auth');
		if (!message.trim()) return;
		setSending(true);
		try {
			await messageAPI
				.send({
					recipient_id: ad.user_id,
					content: message,
					ad_id: ad.id,
					ad_type: adType
				});
			toast.success(t('common.message_sent'));
			setMessage('');
		} catch {
			toast.error(t('common.message_error'));
		}
		setSending(false);
	};

	const upgradeToFeatured = async () => {
		if (!user) return navigate('/auth');
		try {
			ad.is_featured = true;
			await resolveApi(adType).update(ad.id, ad, { pending_payment: true });

			const lineItems = [];
			lineItems.push({
				type: 'featured_ad',
				label: t('horse.featured_cost'),
				quantity: 1
			});

			navigate('/payment/checkout', {
				state: { lineItems, total: pricing.price_featured_ad, adType: adType, adId: ad.id, vat: pricing.vat_percent }
			});
		} catch {
			toast.error(t('verification.payment_error'));
		}
	};

	const boardingTypes = ad.boarding_types || [];
	const disciplines = ad.disciplines || [];
	const isMultiDay = ad.end_date && ad.end_date !== ad.start_date;

	return (
		<div className="lg:col-span-1">
			<div className="sticky top-24 space-y-4">
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
					{ ad.category !== null &&
						<span className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
							{ ad.category === OTHER ? ad.customCategory : catMap[ad.category] || ad.category }
						</span>
					}
					{ ad.vehicle_type &&
						<span className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
							{t(`transport.vehicle.${ad.vehicle_type}`, ad.vehicle_type)}
						</span>
					}
					{ adType === 'stable' && boardingTypes.length > 0 &&
						boardingTypes.slice(0, 3).map(b => (
							<span key={ b }
										className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
								{ t(`stable.boarding_opt.${ b }`, b) }
							</span>
						))
					}
					{ (adType === 'school' || adType === 'trainer') &&
						disciplines.slice(0, 3).map(d => (
							<span key={ d } className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider mr-2">
								{ t(`school.discipline_opt.${ d }`, d) }
							</span>
						))
					}
					{ (adType === 'event' && ad.is_past) &&
						<div className="text-[10px] font-bold bg-[#9B2C2C] text-white px-2 py-0.5 rounded-sm uppercase
								tracking-wider flex items-center gap-2" data-testid="event-past-notice">
							<AlertCircle className="w-4 h-4 mr-3" />
							{t('event.past_notice')}
						</div>
					}
					{ adType === 'event' &&
						<span className="text-[10px] font-bold bg-[#F2F0E9] text-[#1A3C34] px-2 py-0.5 rounded-sm uppercase
										tracking-wider">
							{t(`event.type.${ad.event_type || 'other'}`)}
						</span>
					}
					{ (adType === 'vet' && ad.clinic_name) &&
						<span className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
							{ad.clinic_name}
						</span>
					}
					{ad.level && (
						<span className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
							{ t(`event.level_opt.${ ad.level }`) }
						</span>
					)}
					<div className="flex items-start justify-between mb-4">
						<div>
							<h1 className="text-2xl font-bold text-[#1A3C34] font-['Playfair_Display'] mb-1">{ ad.title }</h1>
							{ (adType === 'horse' || adType === 'product' || adType === 'transport' || adType === 'school' || adType === 'trainer' || adType === 'stable') &&
								<p className="text-3xl font-bold text-[#8B4513]">&euro;{ ad.price?.toLocaleString() }</p>
							}
							{ adType === 'transport' &&
								<p className="text-xs text-[#666055]">/ {t(`transport.unit.${ad.pricing_unit || 'trip'}`)}</p>
							}
							{ (adType === 'school' || adType === 'trainer') &&
								<p className="text-xs text-[#666055]">/ {t(`school.unit.${ad.pricing_unit || 'lesson'}`)}</p>
							}
							{ adType === 'stable' &&
								<p className="text-xs text-[#666055]">/ {t(`stable.unit.${ad.pricing_unit || 'month'}`)}</p>
							}
							{ adType === 'event' && (
								ad.is_free ? (
										<p className="text-3xl font-bold text-[#1A3C34]">{t('event.free')}</p>
									) : (
										<>
											<p className="text-3xl font-bold text-[#8B4513]">&euro;{ad.price?.toLocaleString()}</p>
											<p className="text-xs text-[#666055]">{t('event.entry')}</p>
										</>
									)
								)
							}
							{	adType === 'vet' &&
								<div>
									<p className="text-3xl font-bold text-[#8B4513]">&euro;{(ad.price || 0).toLocaleString()}</p>
									<p className="text-xs text-[#666055]">/ {t('vet.consultation')}</p>
								</div>
							}
						</div>
						<button onClick={ toggleFav }
										className={ `p-2 rounded-sm border transition-colors ${ isFav ? 
											'bg-red-50 border-red-200 text-red-500' : 'border-[#E6E2D6] text-[#666055] hover:text-[#8B4513]' }` }
										data-testid="favorite-btn">
							<Heart className={ `w-5 h-5 ${ isFav ? 'fill-current' : '' }` }/>
						</button>
					</div>
					<div className="flex flex-wrap items-center gap-4 text-sm text-[#666055] mt-3 mb-4">
						{ ad.location_name &&
							<span className="flex items-center gap-1">
								<MapPin className="w-4 h-4"/>
								{ ad.location_name }
							</span>
						}
						<span className="flex items-center gap-1">
							<Eye className="w-4 h-4"/>
							{ ad.views }
						</span>
						{ (!user || ad.user_id !== user.id) && (
							<ReportButton targetType="ad" targetId={ ad.id } adKind={ adType } />
						) }
					</div>
					{ adType === 'stable' &&
						<div className="flex items-center gap-4 text-sm text-[#666055] mt-3 mb-4">
							{typeof ad.available_boxes === 'number' && (
								<span className="inline-flex items-center gap-1.5">
									<Package className="w-4 h-4" />
									{ad.available_boxes}/{ad.total_boxes || '—'} {t('stable.boxes_available')}
								</span>
							)}
						</div>
					}
					{ adType === 'transport' &&
						<div className="flex flex-wrap gap-4 text-sm text-[#3a3a3a] pt-3 border-t border-[#E6E2D6] mb-4">
							{ad.capacity > 0 && (
								<span className="inline-flex items-center gap-1.5">
									<Users className="w-4 h-4 text-[#666055]" />
									{ad.capacity} {t('transport.horses')}
								</span>
							)}
							{ad.coverage_area && (
								<span className="inline-flex items-center gap-1.5">
									<MapPin className="w-4 h-4 text-[#666055]" />
									{ad.coverage_area}
								</span>
							)}
							{ad.license_number && (
								<span className="text-xs text-[#666055]">
									{t('transport.license')}: <strong>{ad.license_number}</strong>
								</span>
							)}
						</div>
					}
					{ adType === 'school' &&
						<div className="flex flex-wrap gap-4 text-sm text-[#3a3a3a] pt-3 border-t border-[#E6E2D6]">
							<span className="inline-flex items-center gap-1.5 mb-4">
								<Users className="w-4 h-4"/>
								{ ad.school_horses_count || 0 } { t('school.school_horses') }
							</span>
						</div>
					}
					{ adType === 'trainer' &&
						<div className="flex flex-wrap gap-4 text-sm text-[#3a3a3a] pt-3 border-t border-[#E6E2D6]">
							{typeof ad.experience_years === 'number' && ad.experience_years > 0 && (
								<span className="inline-flex items-center gap-1.5">
									<Award className="w-4 h-4 text-[#666055]" />
									{ad.experience_years} {t('trainer.years')}
								</span>
							)}
							{ad.travels_to_client && (
								<span className="inline-flex items-center gap-1.5 text-[#666055]">
									<Truck className="w-4 h-4" />
									{t('trainer.travels_to_client')}
								</span>
							)}
						</div>
					}
					{ adType === 'event' &&
						<div className="flex flex-wrap gap-4 text-sm text-[#3a3a3a] pt-3 border-t mb-3 border-[#E6E2D6]">
							<span className="flex items-center gap-1">
								<CalendarDays className="w-4 h-4 text-[#666055]"/>
								{ formatEventDate(ad.start_date) }{ isMultiDay && <> – { formatEventDate(ad.end_date) }</> }
							</span>
							{ad.start_time && (
								<span className="flex items-center gap-1">
									<Clock className="w-4 h-4 text-[#666055]" />
									{ad.start_time}
								</span>
							)}
							{typeof ad.max_participants === 'number' && ad.max_participants > 0 && (
								<span className="flex items-center gap-1">
								<Users className="w-4 h-4 text-[#666055]" />
									{ad.max_participants} {t('event.max_participants_short')}
							</span>
							)}
							{typeof ad.prize_amount === 'number' && ad.prize_amount > 0 && (
								<span className="flex items-center gap-1">
								<Trophy className="w-4 h-4 text-[#666055]" />
									&euro;{ad.prize_amount?.toLocaleString()} {t('event.prize_short')}
							</span>
							)}
						</div>
					}
					{ (adType === 'event' && !ad.is_past) && (
						<button
							onClick={() => downloadICS(ad, window.location.origin)}
							className="inline-flex items-center gap-1.5 ml-auto text-xs bg-[#F2F0E9] hover:bg-[#E6E2D6]
										text-[#1A3C34] px-3 py-1.5 rounded-sm font-semibold uppercase tracking-wider transition-colors mb-3"
							data-testid="event-add-to-calendar"
							title={t('event.add_to_calendar_hint')}
						>
							<CalendarPlus className="w-3.5 h-3.5" /> {t('event.add_to_calendar')}
						</button>
					)}
					{ adType === 'vet' &&
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2 mb-1 flex-wrap">
								{ad.emergency_24_7 && (
									<span className="text-[10px] font-bold bg-[#9B2C2C] text-white px-2 py-0.5 rounded-sm uppercase
											tracking-wider inline-flex items-center gap-1">
										<AlertCircle className="w-3 h-3" /> {t('vet.emergency_24_7_badge')}
									</span>
								)}
								{ad.mobile_only && (
									<span className="text-[10px] font-bold bg-[#F2F0E9] text-[#1A3C34] px-2 py-0.5 rounded-sm
											uppercase tracking-wider inline-flex items-center gap-1">
										<Truck className="w-3 h-3" /> {t('vet.mobile_only_short')}
									</span>
								)}
							</div>
							<div className="flex flex-wrap gap-4 text-sm text-[#3a3a3a] pt-3 border-t border-[#E6E2D6] mb-4">
								{typeof ad.experience_years === 'number' && ad.experience_years > 0 && (
									<span className="inline-flex items-center gap-1.5">
										<GraduationCap className="w-4 h-4 text-[#666055]" />
										{ad.experience_years} {t('vet.years_experience')}
									</span>
								)}
								{ad.license_number && (
									<span className="inline-flex items-center gap-1.5">
										<ShieldCheck className="w-4 h-4 text-[#666055]" />
										{t('vet.license')}: {ad.license_number}
									</span>
								)}
							</div>
						</div>
					}
					<div className="flex items-start justify-between text-sm text-[#666055] mb-4">
						<span className="flex items-center gap-1">
							<CalendarDays className="w-4 h-4"/>
							{ t('horse.ad_updated_at') } { new Date(ad.updated_at).toDateString() }
						</span>
					</div>
					<div className="divider-gold my-4"/>
					{ user && ad.user_id !== user.id ? (
						<div className="space-y-3">
							<Textarea placeholder={ `${ t('horse.contact_seller') }...` } value={ message }
												onChange={ (e) => setMessage(e.target.value) }
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm min-h-[80px] text-sm"
												data-testid="message-input"/>
							<Button onClick={ sendMessage } disabled={ sending || !message.trim() }
											className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-12 font-medium btn-equi"
											data-testid="send-message-btn">
								<MessageSquare className="w-4 h-4 mr-2"/> { t('horse.contact_seller') }
							</Button>
						</div>
					) : !user ? (
						<Link to="/auth">
							<Button className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-12">
								{ t('nav.login') } to { t('horse.contact_seller') }
							</Button>
						</Link>
					) : null }

					{/* Upgrade to Featured */ }
					{ user && ad.user_id === user.id && !ad.is_featured && (
						<>
							<WaiverCheckBox
								images={ [] }
								videos={ [] }
								pricing={ pricing }
								includedImages={ 5 }
								includedVideos={ 3 }
								isFeatured={ true }
								featuredPaid={ false }
								onClick={ setWaivedWithdrawalRight }
								state={ waivedWithdrawalRight }
								t={ t }/>

							<Button onClick={ upgradeToFeatured } disabled={ !waivedWithdrawalRight }
											className="w-full mt-3 bg-[#666055] hover:bg-[#6F3610] text-white rounded-sm h-12 font-medium btn-equi"
											data-testid="upgrade-featured-btn">
								<Zap className="w-4 h-4 mr-2"/> { t('payment.upgrade') } - { pricing.price_featured_ad } EUR
							</Button>
						</>
					) }

				</div>
				{ ad.seller && (
					<div className="bg-white border border-[#E6E2D6] rounded-sm p-6" data-testid="seller-card">
						<Link
							to={ ad.seller.slug ? `/u/${ ad.seller.slug }` : `/u/ ${ ad.seller.id }` }
							className="flex items-center gap-3 group"
							data-testid="seller-profile-link"
						>
							<div className="w-12 h-12 rounded-full bg-[#1A3C34] flex items-center justify-center text-white
										text-lg font-semibold flex-shrink-0">
								{ (ad.seller.company_name || ad.seller.name)?.charAt(0).toUpperCase() }
							</div>
							<div className="min-w-0">
								<p className="font-semibold text-[#1A3C34] group-hover:text-[#666055] transition-colors truncate">
									{ ad.seller.company_name || ad.seller.name }
								</p>
								<div className="flex items-center gap-2 flex-wrap mt-1">
									<SellerRatingBadge avg={ ad.seller.rating_avg || 0 } count={ ad.seller.rating_count || 0 }
																		 size="sm"/>
									{ ad.seller.is_verified && (
										<span className="text-[10px] text-[#1A3C34] inline-flex items-center gap-0.5">
											<BadgeCheck className="w-3 h-3"/> { t('seller.verified') }
										</span>
									) }
								</div>
							</div>
						</Link>
						{ ad.seller.slug && (
							<Link
								to={ `/u/${ ad.seller.slug }` }
								className="mt-3 block text-xs text-[#666055] hover:text-[#6F3610] font-medium mb-2"
								data-testid="view-seller-profile-link"
							>
								{ t('seller.view_profile') } →
							</Link>
						) }
						{ad.contact_phone && (
							<a href={`tel:${ad.contact_phone}`}
								 className="flex items-center gap-2 text-sm text-[#1A3C34] hover:underline mb-2"
								 data-testid="school-phone-link">
								<Phone className="w-4 h-4" /> {ad.contact_phone}
							</a>
						)}
						{ad.website && (
							<a href={ad.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm
									text-[#1A3C34] hover:underline mb-3 truncate">
								<Globe className="w-4 h-4 shrink-0" /> <span className="truncate">{ad.website}</span>
							</a>
						)}
					</div>
				) }
			</div>
		</div>
	);
};